import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { cx } from './cx';

type C1SkeletonVariant = 'text' | 'tile' | 'chart' | 'table' | 'block';

interface C1SkeletonProps {
  variant?: C1SkeletonVariant;
  /** Lines for 'text', tiles for 'tile', body rows for 'table'. */
  count?: number;
  /** Columns for 'table'. */
  columns?: number;
  /** Height for 'block' and 'chart'. */
  height?: number | string;
  /**
   * What is loading, announced once. Pass `null` when another skeleton on the same
   * screen already says it — several live regions all announcing "Loading" talk
   * over each other, and the reader learns nothing from the second one.
   */
  label?: ReactNode | null;
  className?: string;
}

/**
 * Placeholder shapes for content that has not arrived.
 *
 * WHY SHAPES RATHER THAN THE WORD "LOADING". The panels this replaces render a line
 * of text, so the page height jumps when the data lands and whatever the reader was
 * about to click moves out from under the pointer. Blocking out the layout in
 * advance keeps the page still.
 *
 * The shapes are `aria-hidden`: they are a picture of a layout, and describing them
 * is noise. The wrapper is the live region and carries the one sentence worth
 * saying. `aria-busy` marks the region as not yet settled, so assistive technology
 * can wait for it rather than reading a half-built screen.
 */
export function C1Skeleton({
  variant = 'text',
  count = 3,
  columns = 4,
  height,
  label,
  className,
}: C1SkeletonProps) {
  const { t } = useTranslation();
  const message = label === undefined ? t('c1.loading') : label;
  const items = Array.from({ length: Math.max(count, 1) }, (_, index) => index);
  const cells = Array.from({ length: Math.max(columns, 1) }, (_, index) => index);

  return (
    <div
      className={cx('c1-skeleton', `c1-skeleton--${variant}`, className)}
      role="status"
      aria-busy="true"
    >
      {message ? <span className="sr-only">{message}</span> : null}

      <div className="c1-skeletonShapes" aria-hidden="true">
        {variant === 'text' &&
          items.map((index) => (
            <span
              key={index}
              className="c1-skeletonBar"
              // The last line of a paragraph is short. Equal-length bars read as a
              // table of stubs rather than as prose about to appear.
              style={{ width: index === items.length - 1 ? '60%' : '100%' }}
            />
          ))}

        {variant === 'block' && (
          <span className="c1-skeletonBlock" style={{ height: height ?? 120 }} />
        )}

        {variant === 'chart' && (
          <span className="c1-skeletonBlock" style={{ height: height ?? 200 }} />
        )}

        {variant === 'tile' &&
          items.map((index) => (
            <span key={index} className="c1-skeletonTile">
              <span className="c1-skeletonBar" style={{ width: '55%', height: 10 }} />
              <span className="c1-skeletonBar" style={{ width: '40%', height: 24 }} />
            </span>
          ))}

        {variant === 'table' && (
          <span className="c1-skeletonTable">
            <span className="c1-skeletonRow c1-skeletonRow--head">
              {cells.map((cell) => (
                <span key={cell} className="c1-skeletonBar" style={{ height: 10 }} />
              ))}
            </span>
            {items.map((index) => (
              <span key={index} className="c1-skeletonRow">
                {cells.map((cell) => (
                  <span key={cell} className="c1-skeletonBar" />
                ))}
              </span>
            ))}
          </span>
        )}
      </div>
    </div>
  );
}
